import {
  Check,
  Close,
  OpenInNewRounded,
  QuestionMarkRounded,
  SwapHoriz
} from '@mui/icons-material';
import { Avatar, Link, Tooltip, Typography } from '@mui/material';
import FlexContainer from 'components/FlexContainer';
import RelativeDate from 'components/RelativeDate';
import dayjs from 'dayjs';
import { useShowExternalLinkWarningModal } from 'features/fact-grabbing/ExternalLinkWarningModal';
import { MouseEvent } from 'react';
import { ClaimReference } from 'services/backofficeIntegration/http/endpoints/fact-grabbing/httpSearchReferencesByClaim';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import styled from 'styled-components';
import useTr from 'utils/hooks/useTr';

type Props = {
  reference: ClaimReference;
};

export const ClaimReferenceItem = ({ reference }: Props) => {
  const translate = useTr();

  const showModal = useShowExternalLinkWarningModal();

  const handleExternalLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();

    showModal({
      onAccept: () => {
        window.open(reference.source, '_blank', 'noopener,noreferrer');
      }
    });
  };

  return (
    <SourceItem>
      <FlexContainer
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="four"
        fullWidth={true}
      >
        <FlexContainer direction="column" gap="one" fullWidth={true}>
          <FlexContainer direction="row" gap="two" alignItems="center">
            <WebsiteFaviconAvatar>
              {reference.favicon ? (
                <img src={`data:image/png;base64,${reference.favicon}`} alt="Website Favicon" />
              ) : (
                <QuestionMarkRounded fontSize="inherit" />
              )}
            </WebsiteFaviconAvatar>

            <Typography variant="subtitle1" noWrap={true} maxWidth={480}>
              {reference.title}
            </Typography>
          </FlexContainer>

          <GrayLink
            {...withGtmInteraction(gtmIds.aiWriter.factGrabbing.claimReference)}
            href={reference.source}
            onClick={handleExternalLinkClick}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FlexContainer direction="row" gap="two" alignItems="center">
              <Tooltip
                title={translate(
                  'aiWriter.inspirations.factGrabbing.claim_list.chunk.external_link.tooltip'
                )}
              >
                <Typography variant="body2" color="inherit">
                  {new URL(reference.source).hostname}
                </Typography>
              </Tooltip>

              <OpenInNewRounded fontSize="small" />
            </FlexContainer>
          </GrayLink>
        </FlexContainer>

        <SourceIcon>
          {reference.label === 'pro' ? (
            <Check color="success" />
          ) : reference.label === 'con' ? (
            <Close color="error" />
          ) : (
            <SwapHoriz color="warning" />
          )}
        </SourceIcon>
      </FlexContainer>

      <ReferenceText variant="body2">{reference.content}</ReferenceText>

      <Typography variant="body2" color="textSecondary">
        {dayjs(reference.created).format('D MMMM YYYY')}{' '}
        <Typography variant="caption" color="inherit">
          (<RelativeDate date={reference.created} />)
        </Typography>
      </Typography>
    </SourceItem>
  );
};

const SourceItem = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'two',
  justifyContent: 'space-between',
  alignItems: 'flex-start'
})`
  width: 100%;
  padding-block: ${({ theme }) => theme.spacings.three};
`;

const SourceIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WebsiteFaviconAvatar = styled(Avatar)`
  width: 16px;
  height: 16px;
  font-size: 16px;
`;

const GrayLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textSecondary};

  & .MuiSvgIcon-root {
    display: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primaryColorMain};
    text-decoration: none;

    & .MuiSvgIcon-root {
      display: block;
    }
  }
`;

const ReferenceText = styled(Typography)`
  // Ellipsis after 4 lines
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  max-width: 100%;
`;
